import { useMemo } from 'react';

import { isGlobalProject } from 'config';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  LegalClientHeadPositionSection,
  CompanyNameSection,
  CompanyRegistrationAddressSection,
  CompanyResidentialAddressSection,
  CompanyStateRegistrationSection,
  ActivitySection,
  AdditionalDocumentsSection,
} from 'modules/client/common/components/sections';
import { CompanyOrganizationalAndLegalFormSection } from 'modules/client/legal/components/sections';

import {
  CompanyFirstHeadsSection,
  CompanyTaxResidencySection,
} from '../sections';

import { LegalClientLegalHeadSectionsProps } from './types';

export const useLegalClientLegalHeadSections = ({
  countries,
  countriesLoading,
  firstHeads,
  hideActivitySection,
  bodies,
  onClickAddFirstHead,
  onDeleteFirstHead,
  onArchiveFirstHead,
  onDearchiveFirstHead,
  onClickFirstHead,
}: LegalClientLegalHeadSectionsProps) => {
  const { t } = useTranslation();
  const isGlobal = isGlobalProject();

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.legal.legalClientLegalHead.sections.main'),
          id: 'main',
          content: (
            <>
              <CompanyNameSection hideShortName={isGlobal} />
              {isGlobal && (
                <CompanyOrganizationalAndLegalFormSection
                  enabledOrganizationType={false}
                />
              )}
              <CompanyStateRegistrationSection hideDocuments={isGlobal} />
              <CompanyTaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
              />
            </>
          ),
          formSectionKeys: [
            'companyName',
            'organizationalAndLegalForm',
            'companyStateRegistration',
            'companyTaxResidences',
          ],
        },
        {
          label: t('client.legal.legalClientLegalHead.sections.headInfo'),
          id: 'headInfo',
          content: <LegalClientHeadPositionSection bodies={bodies} />,
          formSectionKeys: ['position'],
        },
        {
          label: t('client.legal.legalClientLegalHead.sections.contacts'),
          id: 'contacts',
          content: (
            <>
              <CompanyRegistrationAddressSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <CompanyResidentialAddressSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
            </>
          ),
          formSectionKeys: [
            'companyRegistrationAddress',
            'companyResidentialAddress',
          ],
        },
        {
          label: t('client.legal.legalClientLegalHead.sections.firstHeads'),
          id: 'firstHeads',
          content: (
            <CompanyFirstHeadsSection
              firstHeads={firstHeads}
              onArchiveFirstHead={onArchiveFirstHead}
              onClickAddFirstHead={onClickAddFirstHead}
              onClickFirstHead={onClickFirstHead}
              onDearchiveFirstHead={onDearchiveFirstHead}
              onDeleteFirstHead={onDeleteFirstHead}
            />
          ),
          formSectionKeys: [],
        },
        {
          label: t('client.legal.legalClientLegalHead.sections.documents'),
          id: 'documents',
          content: <AdditionalDocumentsSection />,
          formSectionKeys: ['additionalDocuments'],
        },
        {
          label: t('client.legal.legalClientLegalHead.sections.other'),
          id: 'other',
          content: !hideActivitySection && <ActivitySection isBaseForm />,
          formSectionKeys: ['activity'],
        },
      ]),
    [
      t,
      isGlobal,
      countries,
      countriesLoading,
      bodies,
      firstHeads,
      onArchiveFirstHead,
      onClickAddFirstHead,
      onClickFirstHead,
      onDearchiveFirstHead,
      onDeleteFirstHead,
      hideActivitySection,
    ]
  );

  return { tabs };
};
